import React, { useState, useEffect, useCallback, useMemo } from 'react';
import OurLayouts from "../Layouts";
import { PrimeReactProvider } from "primereact/api";
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import apiService from "../../services/apiservice";
import moment from 'moment';

// Constants
const MAX_DATE_RANGE_MONTHS = 1;
const DATE_FORMAT = 'YYYY-MM-DD';
const DISPLAY_DATE_FORMAT = 'MMM DD, YYYY';

function Downloads() {
  const [loading, setLoading] = useState(false);
  const [filterObj, setFilterObj] = useState({
    fromDate: null,
    toDate: null
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const currentDate = new Date();
    setFilterObj({
      fromDate: moment(currentDate).startOf('month').toDate(),
      toDate: moment(currentDate).endOf('month').toDate()
    });
  }, []);

  // Format date for AP
  const formatDateForAPI = useCallback((date) => {
    if (!date) return '';
    return moment(date).format(DATE_FORMAT);
  }, []);

  // Check if date range exceeds max limit
  const isMoreThanOneMonth = useCallback((fromDate, toDate) => {
    if (!fromDate || !toDate) return false;
    const from = moment(fromDate);
    const to = moment(toDate);
    const diffInMonths = to.diff(from, 'months', true);
    return diffInMonths > MAX_DATE_RANGE_MONTHS;
  }, []);

  // Handle from date change
  const handleFromDateChange = useCallback((e) => {
    const selectedFromDate = e.value;
    setError('');
    setSuccess('');

    if (!selectedFromDate) {
      setFilterObj(prev => ({ ...prev, fromDate: null }));
      return;
    }

    if (filterObj.toDate) {
      const maxAllowedToDate = moment(selectedFromDate).add(MAX_DATE_RANGE_MONTHS, 'month').toDate();
      
      if (moment(filterObj.toDate).isAfter(maxAllowedToDate)) {
        setFilterObj({
          fromDate: selectedFromDate,
          toDate: maxAllowedToDate
        });
      } else {
        setFilterObj(prev => ({
          ...prev,
          fromDate: selectedFromDate
        }));
      }
    } else {
      setFilterObj(prev => ({
        ...prev,
        fromDate: selectedFromDate
      }));
    }
  }, [filterObj.toDate]);

  // Handle to date change
  const handleToDateChange = useCallback((e) => {
    const selectedToDate = e.value;
    setError('');
    setSuccess('');

    if (!selectedToDate) {
      setFilterObj(prev => ({ ...prev, toDate: null }));
      return;
    }

    if (filterObj.fromDate) {
      const maxAllowedToDate = moment(filterObj.fromDate).add(MAX_DATE_RANGE_MONTHS, 'month').toDate();
      
      if (moment(selectedToDate).isAfter(maxAllowedToDate)) {
        setError('Date range cannot exceed 1 month. Please select a valid date range.');
        return;
      }

      if (moment(selectedToDate).isBefore(moment(filterObj.fromDate))) {
        setError('End date cannot be before start date.');
        return;
      }
    }

    setFilterObj(prev => ({
      ...prev,
      toDate: selectedToDate
    }));
  }, [filterObj.fromDate]);

  // Get max allowed to date
  const getMaxToDate = useCallback(() => {
    if (!filterObj.fromDate) return null;
    return moment(filterObj.fromDate).add(MAX_DATE_RANGE_MONTHS, 'month').toDate();
  }, [filterObj.fromDate]);

  // Download file utility
  const downloadFile = useCallback((response, filename) => {
    if (!response?.data) {
      throw new Error('No data received from server');
    }

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = globalThis.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove(); // Use remove() instead of removeChild()
    
    // Revoke URL after a short delay
    setTimeout(() => {
      globalThis.URL.revokeObjectURL(url);
    }, 100);
  }, []);

  // Generic download handler
  const handleDownload = useCallback(async (apiFunction, filenamePrefix, requireDateRange) => {
    if (requireDateRange && (!filterObj.fromDate || !filterObj.toDate)) {
      setError('Please select both from and to dates');
      return;
    }

    if (requireDateRange && isMoreThanOneMonth(filterObj.fromDate, filterObj.toDate)) {
      setError('Date range cannot exceed 1 month');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let response;
      
      if (requireDateRange) {
        const payload = {
          fromDate: formatDateForAPI(filterObj.fromDate),
          toDate: formatDateForAPI(filterObj.toDate)
        };
        response = await apiFunction(payload);
      } else {
        response = await apiFunction();
      }

      const timestamp = Date.now(); // Use Date.now() instead of new Date().getTime()
      const filename = `${filenamePrefix}_${timestamp}.xlsx`;
      
      downloadFile(response, filename);
      setSuccess(`${filenamePrefix} downloaded successfully`);
    } catch (error) {
      console.error(`Error downloading ${filenamePrefix}:`, error);
      setError(`Failed to download ${filenamePrefix}. Please try again.`);
    } finally {
      setLoading(false);
    }
  }, [filterObj, formatDateForAPI, isMoreThanOneMonth, downloadFile]);

  // Specific download handlers
  const handleHospitalDetails = useCallback(() => {
    handleDownload(apiService.downloadHospitalDetails, 'hospital_details', false);
  }, [handleDownload]);

  const handleSurgeryConsents = useCallback(() => {
    handleDownload(apiService.downloadSurgeryConsents, 'surgery_consents', true);
  }, [handleDownload]);

  const handleDischargeSummary = useCallback(() => {
    handleDownload(apiService.downloadDischargeSummary, 'discharge_summary', true);
  }, [handleDownload]);

  // Check if date range is valid
  const isDateRangeValid = useMemo(() => {
    return !isMoreThanOneMonth(filterObj.fromDate, filterObj.toDate) && 
           filterObj.fromDate && 
           filterObj.toDate;
  }, [filterObj.fromDate, filterObj.toDate, isMoreThanOneMonth]);


  // Get formatted date range display
  const formattedDateRange = useMemo(() => {
    if (!filterObj.fromDate || !filterObj.toDate) return null;
    
    const from = moment(filterObj.fromDate).format(DISPLAY_DATE_FORMAT);
    const to = moment(filterObj.toDate).format(DISPLAY_DATE_FORMAT);
    const days = moment(filterObj.toDate).diff(moment(filterObj.fromDate), 'days') + 1;
    const exceedsLimit = isMoreThanOneMonth(filterObj.fromDate, filterObj.toDate);
    
    return { from, to, days, exceedsLimit };
  }, [filterObj.fromDate, filterObj.toDate, isMoreThanOneMonth]);

  return (
    <PrimeReactProvider>
      <OurLayouts>
        <Backdrop
          sx={{ color: "#FFFFFF", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading}
        >
          <CircularProgress size={80} color="inherit" />
        </Backdrop>

        <div className="flex flex-col w-full mt-[7rem] overflow-auto">
          <div className="flex justify-between justify-items-center">
            <div>
              <h1 className="text-2xl text-primary font-semibold mb-8">Downloads</h1>
            </div>
          </div>

          {/* Date Range Filter */}
          <div className='flex items-center gap-4 mr-16 mb-4'>
            <div className="flex flex-col">
              <label htmlFor="fromDate" className="text-sm font-medium mb-2">From Date</label>
              <Calendar 
                id="fromDate"
                className="border border-gray-300 rounded-md p-2" 
                placeholder='Select Start Date'
                onChange={handleFromDateChange} 
                value={filterObj.fromDate}
                name='fromDate' 
                style={{ height: '40px' }}
                showIcon
                icon="pi pi-calendar"
                readOnlyInput
              />
            </div>
            
            <div className="flex flex-col">
              <label htmlFor="toDate" className="text-sm font-medium mb-2">To Date</label>
              <Calendar 
                id="toDate"
                className="border border-gray-300 rounded-md p-2" 
                placeholder='Select End Date'
                onChange={handleToDateChange} 
                value={filterObj.toDate}
                name='toDate' 
                style={{ height: '40px' }}
                showIcon
                icon="pi pi-calendar"
                readOnlyInput
                minDate={filterObj.fromDate}
                maxDate={getMaxToDate()}
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 mr-16">
              <p className="text-red-500 text-sm">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="mb-4 mr-16">
              <p className="text-green-500 text-sm">{success}</p>
            </div>
          )}

          {/* Download Buttons */}
          <div 
            className="w-[99%] h-auto rounded-[10px] p-6" 
            style={{ boxShadow: "0px 8px 25px 0px rgba(0, 0, 0, 0.06)" }}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Hospital Details Button */}
              <div className="flex flex-col items-center">
                <Button
                  label="Hospital Details"
                  icon="pi pi-download"
                  className="w-full p-3 bg-blue-600 text-white border-round"
                  onClick={handleHospitalDetails}
                  disabled={loading}
                  aria-label="Download Hospital Details"
                />
                <small className="mt-2 text-gray-600">No date filter required</small>
              </div>

              {/* Surgery Consents Button */}
              <div className="flex flex-col items-center">
                <Button
                  label="Surgery Consents"
                  icon="pi pi-download"
                  className="w-full p-3 bg-green-600 text-white border-round"
                  onClick={handleSurgeryConsents}
                  disabled={loading || !isDateRangeValid}
                  aria-label="Download Surgery Consents"
                />
                <small className="mt-2 text-gray-600">Max 1 month range allowed</small>
              </div>

              {/* Discharge Summary Button */}
              <div className="flex flex-col items-center">
                <Button
                  label="Discharge Summary"
                  icon="pi pi-download"
                  className="w-full p-3 bg-purple-600 text-white border-round"
                  onClick={handleDischargeSummary}
                  disabled={loading || !isDateRangeValid}
                  aria-label="Download Discharge Summary"
                />
                <small className="mt-2 text-gray-600">Max 1 month range allowed</small>
              </div>
            </div>

{formattedDateRange && (
  <div className="mt-6 text-center">
    <p className="text-sm text-gray-600">
      Selected Date Range:{" "}
      <strong>
        {formattedDateRange.from} - {formattedDateRange.to}
      </strong>
    </p>

    <p className="text-xs text-gray-500">
      Duration: {formattedDateRange.days} day
      {formattedDateRange.days === 1 ? "" : "s"}

      {formattedDateRange.exceedsLimit && (
        <span className="text-red-500 ml-2">
          (Maximum 1 month exceeded)
        </span>
      )}
    </p>
  </div>
)}

          </div>
        </div>
      </OurLayouts>
    </PrimeReactProvider>
  );
}

export default Downloads;